import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock3, MapPin, Phone } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { BookButton, WhatsAppButton, CallButton, DirectionsButton } from "../components/CTAButtons";
import { ZORBA, ALL_SERVICES, SIGNATURE_PACKAGE } from "../data/content";
import { breadcrumbSchema, daySpaSchema, multiGraph } from "../components/Schema";

const notSupplied = "To be confirmed by our partner spa";

const PAGES = {
  services: {
    seoTitle: `${ZORBA.name} Services — Full Menu`,
    description: `All 18 therapies at ${ZORBA.name}, Chandrasekharpur, Bhubaneswar. Verified prices and durations.`,
    title: `${ZORBA.name} services`,
    intro: `The full therapy menu — 18 services spanning classic massage, traditional techniques, hydrotherapy and the signature 6-in-1 wellness experience. Each links to its own page with pricing and preparation notes.`,
    render: () => (
      <div className="service-grid three">
        {ALL_SERVICES.map((s, i) => (
          <Link to={`/services/${s.slug}/`} className="service-card" key={s.slug} data-testid={`zorba-sub-service-${i}`}>
            <div className="service-photo" style={{ backgroundImage: `url(${s.image})` }} />
            <div className="service-card-copy">
              <span className="card-kicker">{s.kicker}</span>
              <h3>{s.name}</h3>
              <p>{s.short}</p>
              <div className="service-price-row">
                <span>{s.priceOptions[0].duration} · {s.priceOptions[0].display}</span>
                <ArrowRight size={18} />
              </div>
            </div>
          </Link>
        ))}
      </div>
    ),
  },
  packages: {
    seoTitle: `${ZORBA.name} Signature Package — 6-in-1 Wellness Experience`,
    description: `Zorba's signature 6-in-1 wellness experience: 90 minutes, ₹6,000. Full inclusions and couple's package options.`,
    title: `Signature package at ${ZORBA.name}`,
    intro: `Zorba's headline wellness journey — the 90-minute 6-in-1 experience, plus a couple's massage package spanning 60 to 120 minutes with a choice of five styles.`,
    render: ({ onBook }) => (
      <div className="package-stack">
        <div className="package-card featured">
          <div className="package-photo" style={{ backgroundImage: `url(${SIGNATURE_PACKAGE.image})` }} />
          <div className="package-body">
            <p className="eyebrow">{SIGNATURE_PACKAGE.kicker}</p>
            <h3>{SIGNATURE_PACKAGE.name}</h3>
            <p className="price-tag">90 min · {SIGNATURE_PACKAGE.priceDisplay}</p>
            <p className="muted">{SIGNATURE_PACKAGE.intro}</p>
            <ul className="check-list">
              {SIGNATURE_PACKAGE.inclusions.map((x) => <li key={x}><Check size={16} />{x}</li>)}
            </ul>
            <div className="actions-row">
              <Link className="btn btn-primary" to={`/services/${SIGNATURE_PACKAGE.slug}/`} data-testid="signature-view-detail">
                Full details <ArrowRight size={16} />
              </Link>
              <WhatsAppButton
                label="Enquire on WhatsApp"
                message={`Hello Zorba, I'd like to book the Signature Therapy (90 min, ₹6,000).`}
                source="zorba_package_signature"
                testid="signature-package-whatsapp"
              />
            </div>
          </div>
        </div>

        <div className="package-card">
          <div className="package-body">
            <p className="eyebrow">Shared ritual</p>
            <h3>Couple Massage</h3>
            <p className="price-tag">60 min · ₹6,500 &nbsp;·&nbsp; 90 min · ₹7,500 &nbsp;·&nbsp; 120 min · ₹12,500</p>
            <p className="muted">
              A private candle-lit couples suite with two therapists. Each partner can pick a different style
              from Aroma, Swedish, Balinese, Deep Tissue or Candle.
            </p>
            <div className="actions-row">
              <Link className="btn btn-primary" to="/services/couple-massage/" data-testid="couples-view-detail">
                Full details <ArrowRight size={16} />
              </Link>
              <WhatsAppButton
                label="Enquire on WhatsApp"
                message={`Hello Zorba, I'd like to enquire about the Couple Massage package.`}
                source="zorba_package_couples"
                testid="couples-package-whatsapp"
              />
            </div>
          </div>
        </div>
      </div>
    ),
  },
  location: {
    seoTitle: `${ZORBA.name} Location — Chandrasekharpur, Bhubaneswar`,
    description: `Visit ${ZORBA.name} at Stalwart Xanadu, Chandrasekharpur, Bhubaneswar. Get directions and plan your visit.`,
    title: `${ZORBA.name} location`,
    intro: `Zorba International Spa is based at Stalwart Xanadu in Chandrasekharpur — easy to reach from Patia, KIIT, Sailashree Vihar, Niladri Vihar, Damana and the wider Infocity area.`,
    render: () => (
      <div className="info-strip">
        <div className="fact-row"><MapPin size={18} /><div><strong>Address</strong><span>{ZORBA.address}</span></div></div>
        <div className="fact-row"><Phone size={18} /><div><strong>Phone / WhatsApp</strong><span>{ZORBA.phoneDisplay}</span></div></div>
        <div className="fact-row"><Clock3 size={18} /><div><strong>Opening hours</strong><span className="muted">{ZORBA.hours || notSupplied}</span></div></div>
        <div className="fact-row"><MapPin size={18} /><div><strong>Area</strong><span>{ZORBA.area}, {ZORBA.city}, {ZORBA.state}, {ZORBA.country} {ZORBA.postalCode}</span></div></div>
        <div className="actions-row">
          <DirectionsButton testid="zorba-location-directions" />
          <CallButton source="zorba_location" testid="zorba-location-call" />
          <WhatsAppButton label="WhatsApp partner" source="zorba_location" testid="zorba-location-whatsapp" />
        </div>
        <div className="map-embed" data-testid="zorba-map-embed">
          <iframe
            title="Zorba International Spa location map"
            src={`https://www.google.com/maps?q=${encodeURIComponent("Zorba International Spa Stalwart Xanadu Chandrasekharpur Bhubaneswar Odisha 751016")}&output=embed`}
            loading="lazy"
            allowFullScreen=""
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    ),
  },
  contact: {
    seoTitle: `${ZORBA.name} Contact — Enquiry & Booking`,
    description: `Reach ${ZORBA.name} on ${ZORBA.phoneDisplay} or by WhatsApp. Chandrasekharpur, Bhubaneswar.`,
    title: `Contact ${ZORBA.name}`,
    intro: `The quickest way to confirm an appointment is a short WhatsApp message. Share your preferred service, approximate time and number of guests — Zorba will take it from there.`,
    render: ({ onBook }) => (
      <div className="info-strip">
        <div className="fact-row"><Phone size={18} /><div><strong>Phone</strong><span>{ZORBA.phoneDisplay}</span></div></div>
        <div className="fact-row"><MapPin size={18} /><div><strong>Address</strong><span>{ZORBA.address}</span></div></div>
        <div className="fact-row"><Clock3 size={18} /><div><strong>Hours</strong><span className="muted">{ZORBA.hours || notSupplied}</span></div></div>
        <div className="actions-row">
          <WhatsAppButton label="WhatsApp Zorba" source="zorba_contact" testid="zorba-contact-whatsapp" />
          <CallButton source="zorba_contact" testid="zorba-contact-call" />
          <BookButton onBook={onBook} service={`${ZORBA.name} enquiry`} label="Open enquiry form" testid="zorba-contact-book" />
          <DirectionsButton testid="zorba-contact-directions" />
        </div>
      </div>
    ),
  },
};

export default function ZorbaSubpage({ page, onBook }) {
  const data = PAGES[page];
  if (!data) return null;
  const titleLabel = data.title
    .replace(`${ZORBA.name} services`, "Services")
    .replace(`Signature package at ${ZORBA.name}`, "Signature package")
    .replace(`${ZORBA.name} location`, "Location")
    .replace(`Contact ${ZORBA.name}`, "Contact");
  const crumbs = [
    { label: "Home", href: "/" },
    { label: ZORBA.name, href: "/zorba-international-spa/" },
    { label: titleLabel },
  ];

  return (
    <>
      <Seo
        title={data.seoTitle}
        description={data.description}
        path={`/zorba-${page}/`}
        jsonLd={multiGraph(daySpaSchema(), breadcrumbSchema(crumbs))}
      />
      <section className="content-hero section-pad narrow">
        <Breadcrumbs items={crumbs} />
        <p className="eyebrow">{ZORBA.disclosure}</p>
        <h1>{data.title}</h1>
        <p className="large-copy">{data.intro}</p>
      </section>
      <section className="section-pad narrow">
        {data.render({ onBook })}
      </section>
    </>
  );
}
